import type { CustomRow } from '../types';
import { formatData } from '../../data-grid';
import {
  BOLD_CLASS,
  CURRENCY_CLASS_PREFIX,
  ITALIC_CLASS,
  PERCENTAGE_CLASS,
  RESIDUAL_LABEL,
  RIGHT_ALIGN_CLASS,
  RISK_FREE_RATE_LABEL,
  TOTAL_LABEL,
  TOTAL_LABEL_CUMULATIVE,
} from '../customAnalysisContants';
import { compact } from 'lodash';
import type { CellClassParams, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import { assertExhaustive, type TabularDataTypeEnum } from 'venn-utils';
import type { CurrencyEnum } from 'venn-api';
import { getCurrencySymbol } from '../components/charts/chartsUtils';
import { cn } from '@/lib/utils';

const isNumericData = (dataType: TabularDataTypeEnum | undefined) => {
  switch (dataType) {
    case 'PERCENTAGE':
    case 'NUMERIC':
    case 'CURRENCY':
      return true;
    case 'TEXT':
    case undefined:
      return false;
    default:
      return assertExhaustive(dataType);
  }
};

/**
 * Helper function to get the currency class string for a given currency
 */
const getCurrencyClassString = (workspaceCurrency?: CurrencyEnum) => {
  const currencySymbol = workspaceCurrency ? getCurrencySymbol(workspaceCurrency) : '$';
  return `${CURRENCY_CLASS_PREFIX}${currencySymbol}`;
};

export const getCellClassForDataType = (
  dataType: TabularDataTypeEnum | undefined,
  workspaceCurrency?: CurrencyEnum,
) => {
  switch (dataType) {
    case 'TEXT':
      return 'TEXT';
    case 'NUMERIC':
      return 'NUMERIC';
    case 'PERCENTAGE':
      return PERCENTAGE_CLASS;
    case 'CURRENCY':
      return getCurrencyClassString(workspaceCurrency);
    case undefined:
      return '';
    default:
      return assertExhaustive(dataType);
  }
};

const getType = ({
  data,
  colDef,
  value,
}: Pick<ValueFormatterParams, 'data' | 'colDef' | 'value'>): TabularDataTypeEnum | undefined =>
  (data && 'type' in data && data.type) ||
  (typeof colDef?.cellRendererParams === 'function'
    ? colDef?.cellRendererParams?.({ data, value })?.type
    : colDef?.cellRendererParams?.type);

/** Selected such that 0.005% (which is 0.00005) can be shown as rounded 0.01% while 0.0049% which rounds to 0.00% is shown as -- */
const EPSILON = 0.00005;

/** Properly formats percentages, numbers, nil values, and strings. */
export const getDefaultValueFormat = ({
  value,
  data,
  colDef,
  currency,
}: Pick<ValueFormatterParams, 'value' | 'data' | 'colDef'> & {
  currency?: CurrencyEnum;
}) => {
  const skipKSuffixInFormatting = (colDef as { skipKSuffixInFormatting?: boolean })?.skipKSuffixInFormatting;
  const dataType = getType({ data, colDef, value });
  let displayValue = value;
  if (colDef?.cellRendererParams?.formatZeroAsUndefined) {
    displayValue = isNumericData(dataType) && Math.abs(value) < EPSILON ? undefined : value;
  }
  return isNumericData(dataType)
    ? formatData({ num: displayValue, dataType, precision: 2, maxChars: 8, currency, skipKSuffixInFormatting })
    : displayValue;
};

/**
 * Cell classes for data types, bold and italics, and excel export styling.
 *
 * TODO(collin.irwin): add getDefaultCellClass to the defaultColDef in columnUtils
 */
export const getDefaultCellClass = <V, T extends CustomRow<V>>({ data, colDef }: CellClassParams<T>): string[] =>
  compact([
    (data && 'type' in data && data.type) || getCellClassForDataType(colDef?.cellRendererParams?.type),
    data && 'label' in data && getSpecialClass(data.label),
    getSpecialClass(colDef.headerName),
    RIGHT_ALIGN_CLASS,
  ]);

export const getSpecialClass = (label?: string) =>
  label === TOTAL_LABEL || label === TOTAL_LABEL_CUMULATIVE
    ? BOLD_CLASS
    : label === RESIDUAL_LABEL || label === RISK_FREE_RATE_LABEL
      ? ITALIC_CLASS
      : undefined;

/**
 * Util to apply the correct currency symbol class
 */
export const getCurrencyCellClass = (workspaceCurrency?: CurrencyEnum) => {
  return () => compact([getCurrencyClassString(workspaceCurrency), RIGHT_ALIGN_CLASS]);
};

interface ChangesCellRendererProps extends ICellRendererParams {
  data: { type?: TabularDataTypeEnum; isMetadata?: boolean };
}

/**
 * Custom column definition for the Changes column used across holdings blocks
 */
export const getChangesColumnDef = () => {
  return {
    headerName: 'Changes',
    field: 'changes',
    type: 'numericColumn',
    headerClass: 'bg-venn-grey-10 text-venn-black pointer-events-none',
    valueFormatter: getDefaultValueFormat,
    cellRenderer: (params: ChangesCellRendererProps) => {
      if (params?.data?.type === 'TEXT') return '--';
      if (params?.data?.isMetadata) return '';
      return params?.value && params.value > 0 ? `+${params.valueFormatted}` : params.valueFormatted;
    },
    cellClass: ({ rowIndex }: { rowIndex: number }) => {
      const bgClass = rowIndex % 2 === 0 ? 'ag-changes-cell-even' : 'ag-changes-cell-odd';
      return cn(bgClass, 'text-right');
    },
    cellRendererParams: {
      formatZeroAsUndefined: true,
    },
  };
};
